<template>
    <nav class="app-main-menu">
        <div v-for="item in items" :key="item.label">
            <app-main-menu-item
                v-if="!item.items"
                v-show="item.isVisible"
                v-bind="item"
                :showText="isSmall && !isTablet"
            ></app-main-menu-item>

            <app-main-menu-group
                v-else
                v-show="item.isVisible"
                v-bind="item"
                :showText="isSmall && !isTablet"
            ></app-main-menu-group>
        </div>
    </nav>
</template>

<script>
    import AppMainMenuGroup from 'components/layouts/components/main-layout/AppMainMenuGroup'
    import AppMainMenuItem from 'components/layouts/components/main-layout/AppMainMenuItem'
    import CalendarTypeEnum from 'enums/calendar_type.enum'
    import RoutesEnum from 'enums/routes.enum'
    import ReservationTypeEnum from 'enums/reservation_type.enum'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    export default {
        mixins: [WindowResizeMixin],

        components: {
            AppMainMenuGroup,
            AppMainMenuItem,
        },

        props: {
            isSmall: { type: Boolean, default: false },
        },

        computed: {
            items() {
                if (this.$auth.isUserAdmin()) {
                    return [
                        {
                            route: {
                                name: RoutesEnum.ADMIN.RESERVATIONS,
                                params: { type: ReservationTypeEnum.RECEIVED },
                                query: { status: 'OPEN' },
                            },
                            label: this.$t('layouts.app.main_menu.admin_reservations'),
                            icon: 'reservation',
                            isVisible: true,
                        },
                        {
                            route: {
                                name: RoutesEnum.ADMIN.ACTIVATE_MEMBER,
                            },
                            label: this.$t('layouts.app.main_menu.activate_member'),
                            icon: 'members',
                            isVisible: this.$auth.isMemberActivated,
                        },
                    ]
                }

                if (this.$auth.isSubdomainActive()) {
                    return [
                        {
                            route: { name: RoutesEnum.SUBDOMAIN.COMPANY },
                            label: this.$t('layouts.app.main_menu.home'),
                            icon: 'menu-home',
                            isVisible: true,
                        },
                        {
                            route: { name: RoutesEnum.SUBDOMAIN.OFFER },
                            label: this.$t('layouts.app.main_menu.offers'),
                            icon: 'offers',
                            isVisible: true,
                        },
                        {
                            route: {
                                name: RoutesEnum.SUBDOMAIN.RESERVATIONS,
                                params: { type: ReservationTypeEnum.MADE },
                                matchFullRoute: true,
                            },
                            label: this.$t('layouts.app.main_menu.reservations'),
                            icon: 'reservation',
                            isVisible: this.isUserLoggedIn,
                        },
                        {
                            route: { name: RoutesEnum.SUBDOMAIN.CONTACT },
                            label: this.$t('layouts.app.main_menu.contact'),
                            icon: 'support',
                            isVisible: true,
                        },
                    ]
                } else {
                    return [
                        {
                            route: { name: RoutesEnum.INDEX.HOME },
                            label: this.$t('layouts.app.main_menu.home'),
                            icon: 'menu-home',
                            isVisible: true,
                        },

                        {
                            route: {
                                name: RoutesEnum.RESERVATIONS.MY,
                                params: { type: ReservationTypeEnum.MADE },
                                matchFullRoute: true,
                            },
                            label: this.$t('layouts.app.main_menu.admin_reservations'),
                            icon: 'reservation',
                            isVisible: this.isUserLoggedIn && !this.isUserHasAnyAvailability,
                        },

                        {
                            label: this.$t('layouts.app.main_menu.reservations'),
                            icon: 'reservation',
                            isVisible: this.isUserLoggedIn && this.isUserHasAnyAvailability,
                            items: [
                                {
                                    route: {
                                        name: RoutesEnum.RESERVATIONS.MY,
                                        params: { type: ReservationTypeEnum.MADE },
                                        matchFullRoute: true,
                                    },
                                    label: this.$t('layouts.app.main_menu.made'),
                                    isVisible: true,
                                },
                                {
                                    route: {
                                        name: RoutesEnum.RESERVATIONS.MY,
                                        params: { type: ReservationTypeEnum.RECEIVED },
                                        matchFullRoute: true,
                                    },
                                    label: this.$t('layouts.app.main_menu.received'),
                                    isVisible: this.isUserHasAnyAvailability,
                                },
                                {
                                    route: {
                                        name: RoutesEnum.RESERVATIONS.TIMELINE,
                                    },
                                    label: this.$t('layouts.app.main_menu.timeline'),
                                    isVisible: this.isUserHasAnyAvailability,
                                },
                            ],
                        },
                        {
                            route: { name: RoutesEnum.CALENDAR.MY, params: { type: CalendarTypeEnum.MONTLY } },
                            label: this.$t('layouts.app.main_menu.my_calendar'),
                            icon: 'calendar',
                            isVisible: this.isUserLoggedIn && this.isUserHasAnyAvailability,
                        },
                        {
                            label: this.$t('layouts.app.main_menu.company'),
                            icon: 'menu-companies',
                            isVisible: this.isUserLoggedIn && this.$auth.getUser().companyUsers.length,
                            items: [
                                {
                                    route: { name: RoutesEnum.COMPANY.MY_COMPANIES },
                                    label: this.$t('layouts.app.main_menu.my_companies'),
                                    isVisible: true,
                                },
                                ...this.companyUsers?.map((cr) => ({
                                    route: {
                                        name: RoutesEnum.COMPANY.MY_COMPANY,
                                        params: { id: cr.company?.id.toString(), tabType: 'profile' },
                                        matchFullRoute: true,
                                    },
                                    label: cr.company?.name,
                                    isSubmenu: true,
                                    isVisible: true,
                                })),
                            ],
                        },

                        {
                            route: { name: RoutesEnum.OFFER.FAVORITES },
                            label: this.$t('layouts.app.main_menu.favorites'),
                            icon: 'heart',
                            isVisible: this.isUserLoggedIn,
                        },
                    ]
                }
            },

            isUserLoggedIn() {
                return this.$auth.isLoggedIn()
            },

            companyUsers() {
                return this.$auth.getUser().companyUsers || []
            },

            isUserHasAnyAvailability() {
                return this.$auth.getUser().hasAnyAvailability
            },
        },
    }
</script>
